/**
 * @class
 * @function MegaMenuSidebar
 *
 * Desktop sidebar mega (thb-full-menu + mega-menu-container--sidebar-with-subheader) — QA DOM map:
 *
 * L1: full-menu > ul.thb-full-menu > li.menu-item-has-megamenu
 *     — label a.thb-full-menu--link--label (navigates); chevron button.thb-full-menu__submenu-toggle selects sidebar L1 / primes L2 content only
 *       (L2 strip visibility: .header--inner hover/peek + scroll collapse — not the chevron),
 *     — mega root div.sub-menu.mega-menu-container.mega-menu-container--sidebar-with-subheader
 *     — selected row: li.jsc-mega-sidebar--default-l1
 *     — URL match (L1 self label or L2-owned subtree): li.jsc-full-menu-l1-subtree-match; chevron-selected: li.jsc-full-menu-l1-toggle-active (#header; label-only, chevron hidden in CSS)
 *
 * L2: div.header--mega-sidebar-subheader, ul.header--mega-sidebar-subheader-nav
 *     — buttons/links .mega-menu-sidebar--button--subheader*, scroll-hide: .jsc-mega-subheader--hidden
 *
 * L3: div.mega-menu-container--inner, mega-menu-sidebar, ul.mega-menu-sidebar--content-inner.active
 *     — open: .jsc-mega-l3-open on inner + mega root; collapse: .jsc-mega-l3-closing on inner until transition ends
 *     — overlay #jsc-mega-l3-overlay (full-viewport; positioned in CSS)
 */

if (!customElements.get('mega-menu-sidebar')) {
  class MegaMenuSidebar extends HTMLElement {
    constructor() {
      super();
      this._l3CloseGen = 0;
    }

    connectedCallback() {
      const rootId = this.id;
      this.buttons = rootId
        ? document.querySelectorAll(`[data-mega-sidebar-root="${rootId}"].mega-menu-sidebar--button`)
        : this.querySelectorAll('.mega-menu-sidebar--button');
      const contentRoot = this.querySelector('.mega-menu-sidebar--content');
      this.content = [];
      if (contentRoot) {
        for (let i = 0; i < contentRoot.children.length; i++) {
          const el = contentRoot.children[i];
          if (el.classList && el.classList.contains('mega-menu-sidebar--content-inner')) {
            this.content.push(el);
          }
        }
      }
      const useClickTabs = Boolean(this.closest('.mobile-menu-drawer'));
      const isDesktopFullMenu = !useClickTabs && Boolean(this.closest('.thb-full-menu'));

      if (useClickTabs) {
        this.buttons.forEach((button, i) => {
          if (button.tagName !== 'BUTTON') return;
          const activate = () => {
            this.onHover(null, button);
            this.buttons.forEach((b, j) => {
              if (b.tagName !== 'BUTTON') return;
              b.setAttribute('aria-selected', j === i ? 'true' : 'false');
            });
          };
          button.addEventListener('mouseover', activate);
          button.addEventListener('focusin', activate);
          button.addEventListener('click', (e) => {
            e.preventDefault();
            activate();
          });
        });
      } else if (isDesktopFullMenu) {
        this._desktopL3Target = null;
        this._desktopL3Open = false;
        this._l3CloseTransitionEnd = null;
        this._l3CloseListenerInner = null;
        this._l3CloseTimer = null;
        this.buttons.forEach((button) => {
          if (button.tagName !== 'BUTTON') return;
          button.addEventListener('click', (e) => {
            e.preventDefault();
            this._onDesktopL2Click(button);
          });
        });
      } else {
        this.buttons.forEach((button, i) => {
          if (button.tagName !== 'BUTTON') return;
          const activate = () => {
            this.onHover(null, button, i);
          };
          button.addEventListener('mouseover', activate);
          button.addEventListener('focusin', activate);
        });
      }

      this.images = this.querySelectorAll('img');

      window.addEventListener('load', () => {
        this.images.forEach((image) => {
          if (typeof lazySizes !== 'undefined' && lazySizes.loader) {
            lazySizes.loader.unveil(image);
          }
        });
      });
    }

    _invalidatePendingL3Close(inner) {
      this._l3CloseGen = (this._l3CloseGen || 0) + 1;
      if (this._l3CloseTransitionEnd) {
        const el = this._l3CloseListenerInner;
        if (el) el.removeEventListener('transitionend', this._l3CloseTransitionEnd);
        this._l3CloseTransitionEnd = null;
        this._l3CloseListenerInner = null;
      }
      if (this._l3CloseTimer) {
        clearTimeout(this._l3CloseTimer);
        this._l3CloseTimer = null;
      }
      const node = inner || this.closest('.mega-menu-container--inner');
      if (node) {
        node.classList.remove('jsc-mega-l3-closing');
        node.style.removeProperty('--jsc-mega-l3-inner-h');
      }
    }

    _finishL3PanelStateReset() {
      this.content.forEach((c) => c.classList.remove('active'));
      this.buttons.forEach((btn) => {
        btn.classList.remove('active');
        btn.setAttribute('aria-expanded', 'false');
      });
    }

    _finishL3Chrome(overlay, inner, megaRoot) {
      document.body.classList.remove('overflow-hidden');
      if (overlay) {
        overlay.hidden = true;
        overlay.setAttribute('aria-hidden', 'true');
      }
      if (megaRoot) megaRoot.classList.remove('jsc-mega-l3-open');
      if (inner) {
        inner.classList.remove('jsc-mega-l3-closing');
        inner.style.removeProperty('--jsc-mega-l3-inner-h');
      }
      document.documentElement.style.removeProperty('--jsc-mega-l3-overlay-top');
    }

    _onDesktopL2Click(button) {
      const overlay = document.getElementById('jsc-mega-l3-overlay');
      const inner = this.closest('.mega-menu-container--inner');
      const megaRoot = this.closest('.mega-menu-container--sidebar-with-subheader');
      const targetSel = button.getAttribute('data-target');
      const owningL1 = this.closest('.thb-full-menu > li.menu-item-has-megamenu');
      const currentDefaultL1 = document.querySelector('.thb-full-menu > li.jsc-mega-sidebar--default-l1');

      // Only swap primary L1 when the click actually changes it, and reset L3 so
      // the previous L1's stale `.jsc-mega-l3-open` classes/instance state don't linger.
      if (owningL1 && owningL1 !== currentDefaultL1 && typeof window.jscSelectSidebarMegaPrimaryL1 === 'function') {
        window.jscSelectSidebarMegaPrimaryL1(owningL1, { resetL3: true });
      }

      if (this._desktopL3Open && this._desktopL3Target === targetSel) {
        this.closeDesktopL3();
        return;
      }

      this._invalidatePendingL3Close(inner);

      this.onHover(null, button);
      this._desktopL3Target = targetSel;
      this._desktopL3Open = true;

      this.buttons.forEach((btn) => {
        if (btn.tagName !== 'BUTTON') return;
        btn.setAttribute('aria-expanded', btn === button ? 'true' : 'false');
      });

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (inner) {
        inner.classList.remove('jsc-mega-l3-closing');
        if (reduced) {
          inner.style.removeProperty('--jsc-mega-l3-inner-h');
          inner.classList.add('jsc-mega-l3-open');
        } else {
          inner.style.setProperty('--jsc-mega-l3-inner-h', '0px');
          inner.classList.add('jsc-mega-l3-open');
        }
      }
      if (megaRoot) megaRoot.classList.add('jsc-mega-l3-open');
      document.body.classList.add('overflow-hidden');
      if (overlay) {
        overlay.hidden = false;
        overlay.setAttribute('aria-hidden', 'false');
      }
      if (reduced || !inner) {
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const h = inner.scrollHeight;
          inner.style.setProperty('--jsc-mega-l3-inner-h', `${Math.max(h, 1)}px`);
        });
      });
    }

    closeDesktopL3() {
      if (!this.closest('.thb-full-menu')) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const overlay = document.getElementById('jsc-mega-l3-overlay');
      const inner = this.closest('.mega-menu-container--inner');
      const megaRoot = this.closest('.mega-menu-container--sidebar-with-subheader');

      this._desktopL3Open = false;
      this._desktopL3Target = null;

      const wasOpen = inner && inner.classList.contains('jsc-mega-l3-open');
      const wasClosing = inner && inner.classList.contains('jsc-mega-l3-closing');

      if (!wasOpen && !wasClosing) {
        this._finishL3PanelStateReset();
        this._finishL3Chrome(overlay, inner, megaRoot);
        return;
      }

      if (wasClosing) {
        if (this._l3CloseTransitionEnd && inner) {
          inner.removeEventListener('transitionend', this._l3CloseTransitionEnd);
          this._l3CloseTransitionEnd = null;
          this._l3CloseListenerInner = null;
        }
        if (this._l3CloseTimer) {
          clearTimeout(this._l3CloseTimer);
          this._l3CloseTimer = null;
        }
        this._l3CloseGen++;
        inner.classList.remove('jsc-mega-l3-open', 'jsc-mega-l3-closing');
        this._finishL3PanelStateReset();
        this._finishL3Chrome(overlay, inner, megaRoot);
        return;
      }

      if (reduced || !inner) {
        inner?.classList.remove('jsc-mega-l3-open', 'jsc-mega-l3-closing');
        this._finishL3PanelStateReset();
        this._finishL3Chrome(overlay, inner, megaRoot);
        return;
      }

      const gen = ++this._l3CloseGen;

      inner.style.setProperty('--jsc-mega-l3-inner-h', `${inner.scrollHeight}px`);
      void inner.offsetHeight;
      inner.classList.remove('jsc-mega-l3-open');
      inner.classList.add('jsc-mega-l3-closing');

      const onEnd = (ev) => {
        if (gen !== this._l3CloseGen) return;
        if (ev) {
          if (ev.target !== inner) return;
          if (ev.propertyName !== 'max-height' && ev.propertyName !== 'opacity') return;
        }
        inner.removeEventListener('transitionend', onEnd);
        this._l3CloseTransitionEnd = null;
        this._l3CloseListenerInner = null;
        if (this._l3CloseTimer) {
          clearTimeout(this._l3CloseTimer);
          this._l3CloseTimer = null;
        }
        this._finishL3PanelStateReset();
        this._finishL3Chrome(overlay, inner, megaRoot);
      };

      this._l3CloseTransitionEnd = onEnd;
      this._l3CloseListenerInner = inner;
      inner.addEventListener('transitionend', onEnd);
      this._l3CloseTimer = setTimeout(() => onEnd(null), 760);

      requestAnimationFrame(() => {
        if (gen !== this._l3CloseGen) return;
        inner.style.setProperty('--jsc-mega-l3-inner-h', '0px');
      });
    }

    onHover(event, button, legacyIndex) {
      const targetSel = button && button.getAttribute && button.getAttribute('data-target');
      let targetPanel = null;
      if (targetSel) {
        targetPanel = this.querySelector(targetSel);
      }
      this.content.forEach((content, index) => {
        content.classList.remove('active');
        if (targetPanel) {
          if (content === targetPanel) {
            content.classList.add('active');
          }
        } else if (typeof legacyIndex === 'number' && legacyIndex == index) {
          content.classList.add('active');
        }
      });
      this.buttons.forEach((this_button) => {
        this_button.classList.remove('active');
      });
      const rootId = this.id;
      if (rootId) {
        document
          .querySelectorAll(`[data-mega-sidebar-root="${rootId}"].mega-menu-sidebar--button--subheader`)
          .forEach((this_button) => {
            this_button.classList.remove('active');
          });
      }
      button.classList.add('active');

      const drawer = this.closest('.mobile-menu-drawer');
      if (drawer) {
        const buttonIndex = Array.prototype.indexOf.call(this.buttons, button);
        drawer.querySelectorAll('.jsc-mobile-menu__collection-link').forEach((link, index) => {
          if (index === buttonIndex) {
            link.removeAttribute('hidden');
          } else {
            link.setAttribute('hidden', '');
          }
        });
      } else if (this._desktopL3Open && this.closest('.thb-full-menu')) {
        const inner = this.closest('.mega-menu-container--inner');
        if (inner && inner.classList.contains('jsc-mega-l3-open')) {
          requestAnimationFrame(() => {
            inner.style.setProperty('--jsc-mega-l3-inner-h', `${Math.max(inner.scrollHeight, 1)}px`);
          });
        }
      }
    }
  }
  customElements.define('mega-menu-sidebar', MegaMenuSidebar);
}

(function () {
  const DESKTOP_MQ = '(min-width: 1068px)';
  /** Kept for theme :has() hooks; sidebar mega no longer toggles this for L2 visibility. */
  const L1_INNER_PANEL_OPEN = 'jsc-mega-l1-inner-open';
  const reduceMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

  function isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  /** True while desktop L3 panel/overlay is open or finishing its close transition — L2 strip must stay expanded. */
  function isDesktopMegaL3BlockingL2Collapse() {
    if (!isDesktop()) return false;
    const overlay = document.getElementById('jsc-mega-l3-overlay');
    if (overlay && !overlay.hidden) return true;
    return Boolean(
      document.querySelector(
        '.thb-full-menu .mega-menu-container--inner.jsc-mega-l3-open, .thb-full-menu .mega-menu-container--inner.jsc-mega-l3-closing'
      )
    );
  }

  function animateMegaSubheaderCollapse(sub) {
    if (isDesktopMegaL3BlockingL2Collapse()) {
      syncThemeHeaderL2StripBorderAttr();
      return;
    }
    if (!sub || sub.classList.contains('jsc-mega-subheader--hidden')) {
      syncThemeHeaderL2StripBorderAttr();
      return;
    }
    if (reduceMotionMq.matches) {
      sub.classList.add('jsc-mega-subheader--hidden');
      syncThemeHeaderL2StripBorderAttr();
      return;
    }
    const h = Math.ceil(sub.scrollHeight) + 2;
    sub.style.maxHeight = `${h}px`;
    void sub.offsetHeight;
    sub.classList.add('jsc-mega-subheader--hidden');
    syncThemeHeaderL2StripBorderAttr();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      sub.removeEventListener('transitionend', onEnd);
      sub.style.maxHeight = '';
      syncThemeHeaderL2StripBorderAttr();
    };
    const onEnd = (e) => {
      if (e.target !== sub || e.propertyName !== 'max-height') return;
      finish();
    };
    sub.addEventListener('transitionend', onEnd);
    setTimeout(finish, 600);
  }

  function animateMegaSubheaderExpand(sub) {
    if (!sub || !sub.classList.contains('jsc-mega-subheader--hidden')) {
      syncThemeHeaderL2StripBorderAttr();
      return;
    }
    if (reduceMotionMq.matches) {
      sub.classList.remove('jsc-mega-subheader--hidden');
      syncThemeHeaderL2StripBorderAttr();
      return;
    }
    sub.classList.remove('jsc-mega-subheader--hidden');
    syncThemeHeaderL2StripBorderAttr();
    sub.style.maxHeight = 'none';
    const h = Math.ceil(sub.scrollHeight) + 2;
    sub.style.maxHeight = '0px';
    void sub.offsetHeight;
    sub.style.maxHeight = `${h}px`;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      sub.removeEventListener('transitionend', onEnd);
      sub.style.maxHeight = '';
      syncThemeHeaderL2StripBorderAttr();
    };
    const onEnd = (e) => {
      if (e.target !== sub || e.propertyName !== 'max-height') return;
      finish();
    };
    sub.addEventListener('transitionend', onEnd);
    setTimeout(finish, 600);
  }

  function isPdp() {
    return document.body && document.body.classList.contains('template-product');
  }

  /** Drives jsc-reset-styles header bottom border: hide .header--inner seam only while default L2 subheader is visible (non-PDP desktop). */
  function syncThemeHeaderL2StripBorderAttr() {
    const header = document.querySelector('#header.header, theme-header.header');
    if (!header) return;
    if (isPdp() || !isDesktop()) {
      header.removeAttribute('data-jsc-mega-l2-strip-visible');
      return;
    }
    const li = document.querySelector('.thb-full-menu > li.jsc-mega-sidebar--default-l1');
    if (!li) {
      header.removeAttribute('data-jsc-mega-l2-strip-visible');
      return;
    }
    const sub = li.querySelector('.header--mega-sidebar-subheader');
    if (!sub || sub.classList.contains('jsc-mega-subheader--hidden')) {
      header.removeAttribute('data-jsc-mega-l2-strip-visible');
    } else {
      header.setAttribute('data-jsc-mega-l2-strip-visible', 'true');
    }
  }
  window.jscSyncThemeHeaderL2StripBorderAttr = syncThemeHeaderL2StripBorderAttr;

  function clearAllDesktopL3() {
    document.querySelectorAll('.thb-full-menu mega-menu-sidebar').forEach((el) => {
      if (typeof el.closeDesktopL3 === 'function') {
        el.closeDesktopL3();
      }
    });
  }

  function resetAllDesktopL3() {
    document.querySelectorAll('.thb-full-menu mega-menu-sidebar').forEach((el) => {
      if (typeof el._invalidatePendingL3Close === 'function') {
        el._invalidatePendingL3Close();
      }
      el._desktopL3Open = false;
      el._desktopL3Target = null;
      if (typeof el._finishL3PanelStateReset === 'function') {
        el._finishL3PanelStateReset();
      }
      const inner = el.closest('.mega-menu-container--inner');
      const megaRoot = el.closest('.mega-menu-container--sidebar-with-subheader');
      if (inner) {
        inner.classList.remove('jsc-mega-l3-open', 'jsc-mega-l3-closing');
        inner.style.removeProperty('--jsc-mega-l3-inner-h');
      }
      if (megaRoot) megaRoot.classList.remove('jsc-mega-l3-open');
    });

    const overlay = document.getElementById('jsc-mega-l3-overlay');
    if (overlay) {
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('overflow-hidden');
    document.documentElement.style.removeProperty('--jsc-mega-l3-overlay-top');
  }

  function stripSidebarPrimaryClass() {
    document.querySelectorAll('.thb-full-menu > li.jsc-mega-sidebar--default-l1').forEach((el) => {
      el.classList.remove('jsc-mega-sidebar--default-l1', 'jsc-mega-sidebar--default-l1--suppressed');
      delete el.dataset.jscDefaultL2Primed;
    });
  }

  function normalizePathname(path) {
    if (!path || path === '') return '/';
    const withoutQuery = String(path).split('?')[0];
    if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
      return withoutQuery.slice(0, -1);
    }
    return withoutQuery;
  }

  function getAnchorPathname(href) {
    if (!href) return null;
    const h = String(href).trim();
    if (!h || h.startsWith('#') || h.toLowerCase().startsWith('javascript:')) return null;
    try {
      const u = new URL(h, window.location.origin);
      return normalizePathname(u.pathname);
    } catch {
      return null;
    }
  }

  /** Longest pathname among anchors in root that exactly match the current page (for L1 tie-break). */
  function getBestExactPathMatchLenInSubtree(root) {
    const cur = normalizePathname(window.location.pathname);
    let best = 0;
    root.querySelectorAll('a[href]').forEach((a) => {
      const p = getAnchorPathname(a.getAttribute('href'));
      if (p && p === cur) {
        best = Math.max(best, p.length);
      }
    });
    return best;
  }

  /**
   * Anchors that belong to Shopify L2 under this L1 (flyout subtree, mega columns, or sidebar L2 + its L3 panels).
   * Excludes the L1 label link and promo-only mega columns without a nav heading.
   */
  function collectL2OwnedAnchorsForL1Highlight(li) {
    const anchors = new Set();
    const flyout = li.querySelector(':scope > ul.sub-menu:not(.mega-menu-container)');
    const mega = li.querySelector(':scope > .sub-menu.mega-menu-container');

    if (mega && mega.classList.contains('mega-menu-container--sidebar-with-subheader')) {
      const ms = mega.querySelector('mega-menu-sidebar');
      const nav = mega.querySelector('.header--mega-sidebar-subheader-nav');
      if (nav) {
        nav.querySelectorAll('a.mega-menu-sidebar--button--subheader--link[href]').forEach((a) => {
          anchors.add(a);
        });
        nav.querySelectorAll('button.mega-menu-sidebar--button--subheader--has-children[data-target]').forEach((btn) => {
          const sel = btn.getAttribute('data-target');
          const panel = ms && sel ? ms.querySelector(sel) : null;
          if (panel) {
            panel.querySelectorAll('a[href]').forEach((a) => {
              anchors.add(a);
            });
          }
        });
      }
      return Array.from(anchors);
    }

    if (mega) {
      mega.querySelectorAll('ul.mega-menu-columns > li').forEach((colLi) => {
        if (!colLi.querySelector('a.mega-menu-columns__heading')) return;
        colLi.querySelectorAll('a[href]').forEach((a) => {
          anchors.add(a);
        });
      });
      return Array.from(anchors);
    }

    if (flyout) {
      flyout.querySelectorAll('a[href]').forEach((a) => {
        anchors.add(a);
      });
    }
    return Array.from(anchors);
  }

  /** Longest exact pathname match among L1’s own label URL plus L2-owned links (same set as collectL2…). */
  function getBestExactPathMatchLenForL1SelfAndL2Owned(li) {
    const cur = normalizePathname(window.location.pathname);
    let best = 0;
    const label = li.querySelector(':scope > a.thb-full-menu--link--label');
    if (label) {
      const pLabel = getAnchorPathname(label.getAttribute('href'));
      if (pLabel && pLabel === cur) {
        best = Math.max(best, pLabel.length);
      }
    }
    collectL2OwnedAnchorsForL1Highlight(li).forEach((a) => {
      const p = getAnchorPathname(a.getAttribute('href'));
      if (p && p === cur) {
        best = Math.max(best, p.length);
      }
    });
    return best;
  }

  /** Main nav only — avoids `.thb-full-menu` in announcement bar / elsewhere. */
  const HEADER_FULL_MENU = '#header .thb-full-menu';
  const L1_SUBTREE_MATCH = 'jsc-full-menu-l1-subtree-match';
  const L1_TOGGLE_ACTIVE = 'jsc-full-menu-l1-toggle-active';

  function stripFullMenuL1SubtreeHighlight() {
    document.querySelectorAll(`${HEADER_FULL_MENU} > li.${L1_SUBTREE_MATCH}`).forEach((li) => {
      li.classList.remove(L1_SUBTREE_MATCH);
      const label = li.querySelector(':scope > .thb-full-menu--link--label');
      if (label && label.dataset.jscSubtreeAria === '1') {
        label.removeAttribute('aria-current');
        delete label.dataset.jscSubtreeAria;
      }
    });
  }

  function stripFullMenuL1ToggleNavHighlight() {
    document.querySelectorAll(`${HEADER_FULL_MENU} > li.${L1_TOGGLE_ACTIVE}`).forEach((li) => {
      li.classList.remove(L1_TOGGLE_ACTIVE);
    });
  }

  /**
   * Clears URL-based L1 highlight on every other top item, then marks the given L1 (chevron / sidebar select).
   */
  function setL1ChevronNavHighlight(li) {
    const root = document.querySelector(HEADER_FULL_MENU);
    if (!li || !root || !root.contains(li)) return;
    stripFullMenuL1SubtreeHighlight();
    stripFullMenuL1ToggleNavHighlight();
    li.classList.add(L1_TOGGLE_ACTIVE);
  }
  window.jscSetL1ChevronNavHighlight = setL1ChevronNavHighlight;

  function findBestHeaderL1BySubtreeUrlMatch() {
    const items = document.querySelectorAll(`${HEADER_FULL_MENU} > li`);
    let bestLi = null;
    let bestLen = -1;
    items.forEach((li) => {
      const len = getBestExactPathMatchLenForL1SelfAndL2Owned(li);
      if (len > bestLen) {
        bestLen = len;
        bestLi = li;
      }
    });
    return bestLen > 0 ? bestLi : null;
  }

  /**
   * When the current URL matches this L1’s label or an L2-owned link, mark the winning L1 for label-only
   * styling (even if Liquid already set `li.active` / aria-current — theme styles may not match our bar).
   * Chevron is hidden via CSS. Optional aria-current only if Liquid did not set it.
   */
  function applyFullMenuL1SubtreeHighlight() {
    stripFullMenuL1SubtreeHighlight();
    if (document.querySelector(`${HEADER_FULL_MENU} > li.${L1_TOGGLE_ACTIVE}`)) return;
    const li = findBestHeaderL1BySubtreeUrlMatch();
    if (!li) return;
    li.classList.add(L1_SUBTREE_MATCH);
    const label = li.querySelector(':scope > .thb-full-menu--link--label');
    if (label && !label.hasAttribute('aria-current')) {
      label.setAttribute('aria-current', 'page');
      label.dataset.jscSubtreeAria = '1';
    }
  }
  window.jscApplyFullMenuL1SubtreeHighlight = applyFullMenuL1SubtreeHighlight;

  function findSidebarMegaL1ByUrl() {
    const items = document.querySelectorAll(
      '.thb-full-menu > li.menu-item-has-megamenu:has(.mega-menu-container--sidebar-with-subheader)'
    );
    let bestLi = null;
    let bestLen = -1;
    items.forEach((li) => {
      const len = getBestExactPathMatchLenForL1SelfAndL2Owned(li);
      if (len > bestLen) {
        bestLen = len;
        bestLi = li;
      }
    });
    return bestLen > 0 ? bestLi : null;
  }

  function getFirstSidebarMegaL1InDom() {
    return document.querySelector(
      '.thb-full-menu > li.menu-item-has-megamenu:has(.mega-menu-container--sidebar-with-subheader)'
    );
  }

  function getPrimarySidebarMegaL1Candidate() {
    const active = document.querySelector(
      '.thb-full-menu > li.menu-item-has-megamenu.active:has(.mega-menu-container--sidebar-with-subheader)'
    );
    if (active) return active;
    const urlMatch = findSidebarMegaL1ByUrl();
    if (urlMatch) return urlMatch;
    return getFirstSidebarMegaL1InDom();
  }

  /**
   * L2 subheader control (button with L3 panel, or leaf link) that corresponds to the current URL.
   */
  function findL2ControlByCurrentPath(li, ms) {
    const cur = normalizePathname(window.location.pathname);
    const nav = li.querySelector('.header--mega-sidebar-subheader-nav');
    if (!nav || !ms) return null;

    const buttons = nav.querySelectorAll(
      'button.mega-menu-sidebar--button--subheader--has-children[data-target]'
    );
    for (let i = 0; i < buttons.length; i++) {
      const btn = buttons[i];
      const sel = btn.getAttribute('data-target');
      if (!sel) continue;
      const panel = ms.querySelector(sel);
      if (!panel) continue;
      const anchors = panel.querySelectorAll('a[href]');
      for (let j = 0; j < anchors.length; j++) {
        const p = getAnchorPathname(anchors[j].getAttribute('href'));
        if (p && p === cur) {
          return btn;
        }
      }
    }

    const leafLinks = nav.querySelectorAll('a.mega-menu-sidebar--button--subheader--link');
    for (let k = 0; k < leafLinks.length; k++) {
      const link = leafLinks[k];
      const p = getAnchorPathname(link.getAttribute('href'));
      if (p && p === cur) {
        return link;
      }
    }
    return null;
  }

  /** Remove `.active` / aria from L2 subheader controls (and mirrored root buttons); does not touch content panels. */
  function stripSidebarL2SubheaderActive(li) {
    const ms = li.querySelector('mega-menu-sidebar');
    if (!ms) return;
    if (ms.buttons && ms.buttons.forEach) {
      ms.buttons.forEach((b) => b.classList.remove('active'));
    }
    const rootId = ms.id;
    if (rootId) {
      document
        .querySelectorAll(`[data-mega-sidebar-root="${rootId}"].mega-menu-sidebar--button--subheader`)
        .forEach((b) => {
          b.classList.remove('active');
          if (b.tagName === 'BUTTON') b.setAttribute('aria-expanded', 'false');
        });
    }
    li.querySelectorAll('.header--mega-sidebar-subheader-nav .mega-menu-sidebar--button--subheader').forEach((b) => {
      b.classList.remove('active');
      if (b.tagName === 'BUTTON') b.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Desktop load / L1 switch: show the correct L3 column panel without marking any L2 tab `.active`
   * (L3 overlay is closed until the user clicks an L2 control).
   */
  function primeDesktopMegaSidebarContentPanelOnly(ms, button, li) {
    if (!ms || !button || button.tagName !== 'BUTTON' || !li) return;
    const targetSel = button.getAttribute('data-target');
    let targetPanel = null;
    if (targetSel) {
      targetPanel = ms.querySelector(targetSel);
    }
    if (ms.content && ms.content.forEach) {
      ms.content.forEach((content) => {
        content.classList.remove('active');
        if (targetPanel && content === targetPanel) {
          content.classList.add('active');
        }
      });
    } else {
      li.querySelectorAll('.mega-menu-sidebar--content-inner').forEach((content) => {
        content.classList.remove('active');
        if (targetPanel && content === targetPanel) {
          content.classList.add('active');
        }
      });
    }
    stripSidebarL2SubheaderActive(li);
  }

  function clearSidebarMegaL2L3VisualState(li) {
    const ms = li.querySelector('mega-menu-sidebar');
    if (!ms) return;
    if (ms.content && ms.content.forEach) {
      ms.content.forEach((c) => c.classList.remove('active'));
    } else {
      li.querySelectorAll('.mega-menu-sidebar--content-inner').forEach((c) => c.classList.remove('active'));
    }
    stripSidebarL2SubheaderActive(li);
  }

  /**
   * Sync L2/L3 for a sidebar mega L1 at load / L1 switch: pick the correct L3 column from Liquid trail
   * or URL, but never add `.active` to L2 subheader items (L3 overlay opens only after user clicks L2).
   */
  function primeL2ContentForSidebarL1(li) {
    const ms = li.querySelector('mega-menu-sidebar');
    if (!ms || typeof ms.onHover !== 'function') return;

    const activeBtn = li.querySelector(
      '.header--mega-sidebar-subheader-nav button.mega-menu-sidebar--button--subheader.active'
    );
    if (activeBtn && activeBtn.tagName === 'BUTTON') {
      primeDesktopMegaSidebarContentPanelOnly(ms, activeBtn, li);
      return;
    }

    const activeLink = li.querySelector(
      '.header--mega-sidebar-subheader-nav a.mega-menu-sidebar--button--subheader.active'
    );
    if (activeLink) {
      clearSidebarMegaL2L3VisualState(li);
      return;
    }

    const byPath = findL2ControlByCurrentPath(li, ms);
    if (byPath) {
      if (byPath.tagName === 'BUTTON') {
        primeDesktopMegaSidebarContentPanelOnly(ms, byPath, li);
      } else {
        clearSidebarMegaL2L3VisualState(li);
      }
      return;
    }

    clearSidebarMegaL2L3VisualState(li);
  }

  /**
   * Select which L1 owns the L2 strip without changing whether that strip is currently shown.
   */
  function selectSidebarMegaPrimaryL1(li, options = {}) {
    if (!li || !li.querySelector('.mega-menu-container--sidebar-with-subheader')) return;
    const resetL3 = options.resetL3 !== false;
    const previous = document.querySelector('.thb-full-menu > li.jsc-mega-sidebar--default-l1');
    const previousSub = previous && previous.querySelector('.header--mega-sidebar-subheader');
    const wasSubheaderHidden = previousSub && previousSub.classList.contains('jsc-mega-subheader--hidden');
    const nextSub = li.querySelector('.header--mega-sidebar-subheader');

    if (resetL3) {
      resetAllDesktopL3();
    }

    stripSidebarPrimaryClass();

    li.classList.add('jsc-mega-sidebar--default-l1');
    li.dataset.jscDefaultL2Primed = '1';
    if (nextSub && previousSub) {
      nextSub.classList.toggle('jsc-mega-subheader--hidden', Boolean(wasSubheaderHidden));
    }
    primeL2ContentForSidebarL1(li);

    syncSidebarSubmenuToggleAria();
    syncThemeHeaderL2StripBorderAttr();
    setL1ChevronNavHighlight(li);
  }
  window.jscSelectSidebarMegaPrimaryL1 = selectSidebarMegaPrimaryL1;

  window.jscCloseDesktopMegaMenu = function () {
    if (!isDesktop()) return;
    const overlay = document.getElementById('jsc-mega-l3-overlay');
    if (overlay && !overlay.hidden) {
      clearAllDesktopL3();
      return;
    }
    document.querySelectorAll(`.thb-full-menu > li.${L1_INNER_PANEL_OPEN}`).forEach((node) => {
      node.classList.remove(L1_INNER_PANEL_OPEN);
      const t = node.querySelector('.thb-full-menu__submenu-toggle');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
    document.querySelectorAll('.header--mega-sidebar-subheader.jsc-mega-subheader--hidden').forEach((el) => {
      animateMegaSubheaderExpand(el);
    });
    syncThemeHeaderL2StripBorderAttr();
    stripFullMenuL1ToggleNavHighlight();
    applyFullMenuL1SubtreeHighlight();
  };

  function initL1SubmenuToggles() {
    document.querySelectorAll(`${HEADER_FULL_MENU} > li.menu-item-has-children > .thb-full-menu__submenu-toggle`).forEach((btn) => {
      if (btn.dataset.jscL1ToggleClickBound === '1') return;
      btn.dataset.jscL1ToggleClickBound = '1';
      btn.addEventListener('click', (e) => {
        if (!isDesktop()) return;
        const li = btn.closest('li.menu-item-has-children');
        if (!li) return;
        if (li.querySelector('.mega-menu-container--sidebar-with-subheader')) {
          e.preventDefault();
          e.stopPropagation();
          selectSidebarMegaPrimaryL1(li);
        } else {
          setL1ChevronNavHighlight(li);
        }
      });
    });
  }

  function syncSidebarSubmenuToggleAria() {
    document.querySelectorAll('.thb-full-menu > li.menu-item-has-megamenu').forEach((item) => {
      const toggle = item.querySelector('.thb-full-menu__submenu-toggle');
      if (!toggle || !item.querySelector('.mega-menu-container--sidebar-with-subheader')) return;
      toggle.setAttribute('aria-expanded', item.classList.contains('jsc-mega-sidebar--default-l1') ? 'true' : 'false');
    });
  }

  function initDefaultL1MegaSidebarStrip() {
    if (!isDesktop()) {
      stripSidebarPrimaryClass();
      syncThemeHeaderL2StripBorderAttr();
      return;
    }

    const existing = document.querySelector('.thb-full-menu > li.jsc-mega-sidebar--default-l1');
    if (existing) {
      syncSidebarSubmenuToggleAria();
      syncThemeHeaderL2StripBorderAttr();
      return;
    }

    const desired = getPrimarySidebarMegaL1Candidate();
    if (!desired || !desired.querySelector('.header--mega-sidebar-subheader')) {
      syncThemeHeaderL2StripBorderAttr();
      return;
    }

    desired.classList.add('jsc-mega-sidebar--default-l1');
    if (desired.dataset.jscDefaultL2Primed !== '1') {
      desired.dataset.jscDefaultL2Primed = '1';
      primeL2ContentForSidebarL1(desired);
    }
    syncSidebarSubmenuToggleAria();
    syncThemeHeaderL2StripBorderAttr();
  }

  function getDesktopMegaSubheaderForScroll() {
    return document.querySelector(
      '.thb-full-menu > li.jsc-mega-sidebar--default-l1 .header--mega-sidebar-subheader'
    );
  }

  /** Non-PDP: L2 was expanded only because pointer/focus entered .header--inner while scroll-collapsed. */
  let l2PeekExpandedFromHeaderInner = false;

  let scrollLastY = window.scrollY;
  function onMegaScroll() {
    if (!isDesktop()) return;
    const sub = getDesktopMegaSubheaderForScroll();
    if (!sub) {
      scrollLastY = window.scrollY;
      return;
    }
    const y = window.scrollY;
    if (y > scrollLastY + 4) {
      l2PeekExpandedFromHeaderInner = false;
      animateMegaSubheaderCollapse(sub);
    } else if (y < scrollLastY) {
      l2PeekExpandedFromHeaderInner = false;
      animateMegaSubheaderExpand(sub);
    }
    scrollLastY = y;
  }

  /**
   * Non-PDP desktop: when L2 is scroll-collapsed (.jsc-mega-subheader--hidden), show it again while the
   * user hovers or focuses within .header--inner (same idea as PDP, where mega is revealed from header).
   */
  function initHeaderInnerL2Peek() {
    const headerInner = document.querySelector('#header .header--inner');
    if (!headerInner || headerInner.dataset.jscL2PeekBound === '1') return;
    headerInner.dataset.jscL2PeekBound = '1';

    const tryExpand = () => {
      if (isPdp() || !isDesktop()) return;
      const sub = getDesktopMegaSubheaderForScroll();
      if (!sub || !sub.classList.contains('jsc-mega-subheader--hidden')) return;
      animateMegaSubheaderExpand(sub);
      l2PeekExpandedFromHeaderInner = true;
    };

    const tryCollapsePeek = (e) => {
      if (isPdp() || !isDesktop()) return;
      const rt = e.relatedTarget;
      if (rt instanceof Node && headerInner.contains(rt)) return;
      if (!l2PeekExpandedFromHeaderInner) return;
      l2PeekExpandedFromHeaderInner = false;
      const sub = getDesktopMegaSubheaderForScroll();
      if (sub && !sub.classList.contains('jsc-mega-subheader--hidden')) {
        animateMegaSubheaderCollapse(sub);
      }
    };

    headerInner.addEventListener('mouseenter', tryExpand);
    headerInner.addEventListener('mouseleave', tryCollapsePeek);
    headerInner.addEventListener('focusin', tryExpand);
    headerInner.addEventListener('focusout', tryCollapsePeek);
  }

  function initOverlay() {
    const overlay = document.getElementById('jsc-mega-l3-overlay');
    if (!overlay || overlay.dataset.jscBound === '1') return;
    overlay.dataset.jscBound = '1';
    overlay.addEventListener('click', () => {
      clearAllDesktopL3();
    });
  }

  function boot() {
    initL1SubmenuToggles();
    initOverlay();
    initDefaultL1MegaSidebarStrip();
    applyFullMenuL1SubtreeHighlight();
    initHeaderInnerL2Peek();
    window.addEventListener('scroll', onMegaScroll, { passive: true });
    window.matchMedia(DESKTOP_MQ).addEventListener('change', () => {
      initDefaultL1MegaSidebarStrip();
      applyFullMenuL1SubtreeHighlight();
    });

    const obs = new MutationObserver(() => {
      initL1SubmenuToggles();
      initDefaultL1MegaSidebarStrip();
      applyFullMenuL1SubtreeHighlight();
    });
    const fm = document.querySelector('#header .thb-full-menu') || document.querySelector('.thb-full-menu');
    if (fm) {
      obs.observe(fm, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
